import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import axios from "axios";
// If this file sits directly in src/components/, change to "../utils/ErrorModal"
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const headCellSx = {
  py: 1.25,
  px: 2,
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: MUTED,
  "& strong": { fontWeight: 700 },
};
const bodyCellSx = {
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #F1F4F8",
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
};

const LeadStatus = () => {
  const [status, setStatus] = useState("");
  const [statusList, setStatusList] = useState();
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/meta-lead/all`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setStatusList(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchStatus();
  }, []);

  const handleAdd = async () => {
    if (!status.trim()) return;
    try {
      setBtnLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/meta-lead?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      fetchStatus();
      showModal("Status added", "success");
    } catch (error) {
      showModal(error?.response?.data?.Exception || "Error while adding status", "error");
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
    setStatusList((prev) => [...prev, status.trim()]);
    setStatus("");
  };

  return (
    <Box py={2} display="flex" flexDirection="column" gap={2} sx={{ fontFamily: FONT, color: INK, maxWidth: 820 }}>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}
        >
          Manage Lead Status
        </Typography>
      </Box>

      <Box
        display="flex"
        gap={1.25}
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          p: "14px 16px",
        }}
      >
        <input
          type="text"
          placeholder="Enter lead status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
          style={{
            flex: 1,
            minWidth: 0,
            height: 42,
            boxSizing: "border-box",
            padding: "0 12px",
            margin: 0,
            background: "#FFFFFF",
            border: `1px solid ${FIELD_EDGE}`,
            borderRadius: "10px",
            fontFamily: FONT,
            fontSize: 14,
            color: INK,
            outline: "none",
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={btnLoading}
          sx={{
            height: 42,
            px: 3,
            borderRadius: "10px",
            background: PRIMARY,
            color: "#FFFFFF",
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: "#084767", boxShadow: "none" },
            "&.Mui-disabled": { background: PRIMARY, color: "#FFFFFF", opacity: 0.6 },
          }}
        >
          Add
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 72 }}>
                <strong>#</strong>
              </TableCell>
              <TableCell sx={headCellSx}>
                <strong>Status</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {statusList?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ ...bodyCellSx, color: MUTED, py: 2.5, borderBottom: 0 }}>
                  No statuses added yet.
                </TableCell>
              </TableRow>
            ) : (
              statusList?.map((item, index) => (
                <TableRow key={index} sx={{ "&:hover": { background: "#F4F7FA" }, "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: MONO, fontSize: 12.5, color: MUTED }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{item.status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />
    </Box>
  );
};

export default LeadStatus;